import { api, normaliseApiError } from '@/lib/api';

/**
 * Customer-facing support API.
 *
 * ── THE `lastFailure` BUFFER ───────────────────────────────────────────────────────────────
 * The most valuable field on a support ticket is the one a customer can never type: the request
 * id of the call that actually failed. `error.middleware.ts` already returns it in the body of
 * every error response (`error.requestId`), and the API already stamps `x-request-id` on every
 * request — but nothing on the client was reading either, so the bridge from "the barrister says
 * it did not open" to "here is the exact request" did not exist.
 *
 * This module keeps the LAST failure the session saw, in memory only, and the ticket form
 * pre-fills from it. That is what turns a vague report into an actionable one without asking a
 * barrister to understand what a correlation id is.
 *
 * Held in memory deliberately, with no browser-storage persistence of any kind. It is diagnostic
 * scratch for the current session; it must not outlive the tab, follow a user to a shared
 * machine, or persist after sign-out.
 */

export interface CapturedFailure {
  /** Semantic code when the server named one, else the HTTP error code. */
  errorCode: string;
  correlationId: string | null;
  /** What the user was looking at, e.g. { type: 'Document', id } — for relevance scoping. */
  target?: { type: string; id: string };
  /** Human-facing message the user was shown. Used only to pre-fill the description. */
  message: string;
  at: number;
}

let lastFailure: CapturedFailure | null = null;

/** Failures older than this are not offered as context — the user has moved on. */
const FAILURE_TTL_MS = 30 * 60 * 1000;

/**
 * Record a failure the user actually saw. Called by the surfaces that show a failure state
 * (the document viewer today), NOT by a global interceptor: a background poll that 500s is not
 * something the user is complaining about, and pre-filling a ticket with it would be wrong.
 */
export function captureFailureContext(input: Omit<CapturedFailure, 'at'>): void {
  lastFailure = { ...input, at: Date.now() };
}

export function getLastFailure(): CapturedFailure | null {
  if (!lastFailure) return null;
  if (Date.now() - lastFailure.at > FAILURE_TTL_MS) {
    lastFailure = null;
    return null;
  }
  return lastFailure;
}

export function clearLastFailure(): void {
  lastFailure = null;
}

/**
 * Pull the code + request id out of an Axios error.
 *
 * Delegates to `normaliseApiError` so a binary-typed request (the document viewer uses
 * `responseType: 'arraybuffer'`) is decoded rather than read as a plain object. Before that,
 * this returned `HTTP_404` with a null correlation id for the flagship document scenario —
 * the ticket reached Support with no error code and nothing to trace. Caught in browser UAT.
 */
export function failureFromApiError(err: unknown): { errorCode: string; correlationId: string | null; message: string } {
  const info = normaliseApiError(err);
  return {
    // The server's own code is always preferred; HTTP_<status> is a last resort that tells
    // Support "the client could not read a code", which is itself a useful signal.
    errorCode: info.code ?? (info.status ? `HTTP_${info.status}` : 'NETWORK_ERROR'),
    correlationId: info.requestId,
    message: info.message ?? 'The request failed.',
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type SupportStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type SupportCategory = 'billing' | 'technical' | 'data_recovery' | 'security' | 'other';

export interface MyTicketSummary {
  id: string;
  title: string;
  body: string;
  status: SupportStatus;
  priority: string;
  category: SupportCategory;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  reportedErrorCode: string | null;
  reportedCorrelationId: string | null;
  /** Non-internal messages on the thread. Internal Support notes are never counted or returned. */
  _count?: { messages: number };
  /**
   * Support replies that arrived after this user's own last message. Computed server-side by
   * `conversationSignals` — the platform console reads the SAME fact from the other end, so the
   * two sides of one conversation can never show contradictory counts.
   *
   * "Unanswered", not "unread": there is no per-message read receipt in the schema.
   */
  unansweredCount?: number;
}

export interface MyTicketMessage {
  id: string;
  body: string;
  createdAt: string;
  authorType: 'support' | 'customer';
  authorName: string | null;
  isMine: boolean;
}

export interface MyTicket extends MyTicketSummary {
  messages: MyTicketMessage[];
}

// ─── Calls ────────────────────────────────────────────────────────────────────

export async function createSupportTicket(input: {
  title: string;
  body: string;
  category: SupportCategory;
  reportedErrorCode?: string;
  reportedCorrelationId?: string;
  reportedTargetType?: string;
  reportedTargetId?: string;
}): Promise<MyTicketSummary> {
  const res = await api.post('/support', input);
  return res.data.data.ticket;
}

export async function fetchMyTickets(params: { status?: SupportStatus; page?: number } = {}) {
  const res = await api.get('/support', { params });
  return res.data as {
    data: MyTicketSummary[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  };
}

export async function fetchMyTicket(id: string): Promise<MyTicket> {
  const res = await api.get(`/support/${id}`);
  return res.data.data.ticket;
}

export async function replyToMyTicket(id: string, body: string) {
  const res = await api.post(`/support/${id}/replies`, { body });
  return res.data.data as { message: MyTicketMessage; reopened: boolean };
}
